/**
 * NMS Store Launch · core service
 *
 * Shared by every nms-sl-* section on templates/page.store-launch.json.
 * Exposed as window.neemans.storeLaunch once the config + store JSON printed by
 * sections/nms-sl-launch-settings.liquid have been read.
 *
 * - Normalises store records (temporary JSON today, nms_physical_stores metaobject
 *   later; both use the same field keys) into the ordered journey.
 * - Owns cross-section state: active store, user location, intro, bag count.
 * - Cross-section events are DOM CustomEvents on document, named "nms-sl:<type>".
 * - Renders the mobile bottom sheet (visit / store / state views).
 * - Registers section components and handles Shopify theme editor reloads.
 */
(() => {
  window.neemans = window.neemans || {};
  if (window.neemans.storeLaunch) return;

  const STATE_CODES = {
    Gujarat: 'GJ', Maharashtra: 'MH', Karnataka: 'KA', Telangana: 'TG', Punjab: 'PB', Rajasthan: 'RJ',
    'Madhya Pradesh': 'MP', 'Uttar Pradesh': 'UP', Kerala: 'KL', Haryana: 'HR', Goa: 'GA', Bihar: 'BR',
    'Andhra Pradesh': 'AP', Uttarakhand: 'UK', Chhattisgarh: 'CG', 'Tamil Nadu': 'TN', 'West Bengal': 'WB',
    Delhi: 'DL', Odisha: 'OD', Chandigarh: 'CH', Jharkhand: 'JH', Assam: 'AS',
  };
  const TONES = ['#E7E3DA', '#E1E7DF', '#ECE2DA', '#E3E5E9'];
  const MOBILE_MAX = 759;

  const esc = value => String(value ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const fill = (template, vars) => String(template ?? '').replace(/\{(\w+)\}/g, (match, key) => (vars[key] ?? match));
  const pad2 = n => String(n).padStart(2, '0');
  const clamp01 = x => (x < 0 ? 0 : x > 1 ? 1 : x);
  const ease = t => (t < .5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
  const ordinal = n => n + (n % 100 >= 11 && n % 100 <= 13 ? 'th' : ['th', 'st', 'nd', 'rd'][n % 10] || 'th');
  const num = v => (v === null || v === undefined || v === '' || isNaN(+v) ? null : +v);

  const readJSON = id => {
    const el = document.getElementById(id);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (error) {
      console.warn(`[nms-sl] Could not parse #${id}`, error);
      return null;
    }
  };

  const icon = (name, size = 18) =>
    `<svg class="nms-sl-icon" width="${size}" height="${size}" aria-hidden="true" focusable="false"><use href="#nms-sl-i-${name}"></use></svg>`;

  class NMSStoreLaunch {
    constructor(config, records) {
      this.config = config;
      // Shopify's | t filter HTML-escapes translations (Neeman's → Neeman&#39;s); decode once so
      // textContent shows them as-is and esc() doesn't double-escape them.
      const decoder = document.createElement('textarea');
      this.t = Object.fromEntries(Object.entries(config.i18n || {}).map(([key, value]) => {
        if (typeof value !== 'string' || !value.includes('&')) return [key, value];
        decoder.innerHTML = value;
        return [key, decoder.value];
      }));
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.instances = new Map();
      this.activeIndex = 0;
      this.userLocation = null;
      this.introRevealed = false;
      this.bagCount = 0;
      this.film = null;
      this.helpers = { esc, fill, pad2, clamp01, ease, ordinal, icon };

      this.launch = this.#launchInfo();
      this.stores = this.#prepare(Array.isArray(records) ? records : records?.stores || []);
      this.lastIndex = this.stores.length - 1;
      this.launchStore = this.stores[this.lastIndex];
      this.launch.whatsapp = (this.launchStore?.whatsapp || '').replace(/\D/g, '') || config.waFallback || '';

      this.#bindTracking();
      this.#bindSheet();
      this.#startClock();
      window.addEventListener('hashchange', () => this.#fromHash(false));
    }

    /* ---------------- Events ---------------- */

    on(type, handler, signal) {
      document.addEventListener(`nms-sl:${type}`, event => handler(event.detail || {}), signal ? { signal } : undefined);
    }

    emit(type, detail = {}) {
      document.dispatchEvent(new CustomEvent(`nms-sl:${type}`, { detail }));
    }

    track(event, payload = {}) {
      (window.dataLayer = window.dataLayer || []).push({ event, ...payload });
    }

    /* ---------------- Intro ---------------- */

    /** The intro loader's inline script adds this class before first paint. */
    get introActive() {
      return document.documentElement.classList.contains('nms-sl-intro-active');
    }

    /**
     * Run `callback` once the intro has reached `phase`:
     * 'reveal' = the curtain starts lifting, 'done' = the loader is gone.
     * Runs immediately when there is no intro.
     */
    afterIntro(phase, callback, signal) {
      const reached = phase === 'reveal' ? this.introRevealed || !this.introActive : !this.introActive;
      if (reached) {
        callback();
        return;
      }
      document.addEventListener(`nms-sl:intro:${phase}`, () => callback(), { once: true, ...(signal ? { signal } : {}) });
    }

    /* ---------------- Viewport ---------------- */

    isMobile() {
      return window.innerWidth <= MOBILE_MAX;
    }

    /* ---------------- Launch + dates ---------------- */

    #launchInfo() {
      const c = this.config;
      const number = Math.max(2, Math.min(999, parseInt(c.number, 10) || 50));
      const date = c.date || '';
      const d = new Date(`${date}T00:00:00+05:30`);
      const ok = /^\d{4}-\d{2}-\d{2}$/.test(date) && !isNaN(d);
      const fmt = options => (ok ? new Intl.DateTimeFormat('en-IN', { timeZone: 'Asia/Kolkata', ...options }).format(d) : this.t.datePending);
      return {
        number,
        ordinal: ordinal(number),
        date,
        ok,
        ts: ok ? d.getTime() : NaN,
        tagline: c.tagline || '',
        area: (c.area || '').trim(),
        dateLong: fmt({ weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }),
        dateDay: fmt({ weekday: 'long', day: 'numeric', month: 'long' }),
        dateShort: fmt({ day: 'numeric', month: 'short' }),
        dateDM: fmt({ day: 'numeric', month: 'long' }),
        ymd: date.replace(/-/g, ''),
      };
    }

    isLive() {
      const phase = this.config.phase;
      if (phase === 'live') return true;
      if (phase === 'countdown') return false;
      return this.launch.ok && Date.now() >= this.launch.ts;
    }

    countdown() {
      const L = this.launch;
      if (!L.ok) return { ok: false, ms: NaN, text: this.t.datePending, big: '', label: '' };
      const ms = L.ts - Date.now();
      const dd = Math.floor(ms / 864e5);
      const hh = Math.floor((ms % 864e5) / 36e5);
      const mm = Math.floor((ms % 36e5) / 6e4);
      if (ms <= 0 || this.isLive()) return { ok: true, ms: 0, text: fill(this.t.isOpen, { number: L.number }), dd: 0, hh: 0, mm: 0 };
      const text = dd > 0
        ? fill(this.t.countdownDays, { d: dd, h: hh, number: L.number })
        : fill(this.t.countdownHours, { h: hh, m: mm, number: L.number });
      return { ok: true, ms, text, dd, hh, mm };
    }

    /** Opening hours of the launch store, evaluated in India time. */
    liveStatus() {
      const hours = this.launchStore?.hours || '';
      const m = hours.match(/(\d+):(\d+)\s*(AM|PM)\s*to\s*(\d+):(\d+)\s*(AM|PM)/i);
      const toMin = (h, mi, ap) => ((+h % 12) + (/pm/i.test(ap) ? 12 : 0)) * 60 + +mi;
      const label = (h, mi, ap) => `${+h}${mi === '00' ? '' : `:${mi}`} ${ap.toLowerCase()}`;
      const ist = new Date(Date.now() + (330 + new Date().getTimezoneOffset()) * 6e4);
      const now = ist.getHours() * 60 + ist.getMinutes();
      const open = m ? toMin(m[1], m[2], m[3]) : 660;
      const close = m ? toMin(m[4], m[5], m[6]) : 1320;
      return {
        open: now >= open && now < close,
        opens: m ? label(m[1], m[2], m[3]) : '11 am',
        closes: m ? label(m[4], m[5], m[6]) : '10 pm',
      };
    }

    whatsappUrl(message) {
      const n = this.launch.whatsapp;
      return n ? `https://wa.me/${n}${message ? `?text=${encodeURIComponent(message)}` : ''}` : '';
    }

    calendarUrl() {
      const L = this.launch;
      if (!L.ok) return '';
      const end = new Date(L.ts + 864e5 + 330 * 6e4).toISOString().slice(0, 10).replace(/-/g, '');
      const store = this.launchStore || {};
      return 'https://calendar.google.com/calendar/render?action=TEMPLATE' +
        `&text=${encodeURIComponent(fill(this.t.calendarTitle, { ordinal: L.ordinal, area: L.area }))}` +
        `&dates=${L.ymd}/${end}` +
        `&location=${encodeURIComponent(store.address || L.area)}` +
        `&details=${encodeURIComponent(fill(this.t.calendarDetails, { number: L.number, tagline: L.tagline }))}`;
    }

    #startClock() {
      // One shared 30s tick drives every countdown on the page.
      if (!this.launch.ok) return;
      this.clock = window.setInterval(() => {
        if (!document.hidden) this.emit('tick');
      }, 30000);
    }

    /* ---------------- Store data ---------------- */

    #normalize(r) {
      const lat = num(r.latitude);
      const lng = num(r.longitude);
      const images = (Array.isArray(r.images) ? r.images : [])
        .map(img => (typeof img === 'string' ? { src: img, alt: '' } : { src: img?.src || img?.url || '', alt: img?.alt || '' }))
        .filter(img => img.src);
      return {
        handle: r.handle || '',
        code: r.code === null || r.code === undefined ? '' : String(r.code),
        title: r.title || r.store_name || '',
        city: r.city || '',
        state: r.state || '',
        region: r.region_name || '',
        address: r.address || '',
        zip: r.zip || '',
        lat,
        lng,
        phone: String(r.phone || '').replace(/[^\d+]/g, ''),
        hours: r.hours || '',
        rating: +r.rating || 0,
        reviews: +r.review_count || 0,
        map: r.direction_link || (lat !== null && lng !== null ? `https://www.google.com/maps/search/?api=1&query=${lat},${lng}` : ''),
        whatsapp: r.whatsapp_link || '',
        launchDate: r.launch_date || '',
        launchYear: String(r.launch_year || r.launch_date || '').slice(0, 4).replace(/D/g, ''),
        placeId: r.place_id || '',
        images,
      };
    }

    #prepare(raw) {
      const c = this.config;
      const L = this.launch;
      const t = this.t;
      const chapters = c.chapters || {};
      const all = raw.map(r => this.#normalize(r)).filter(s => s.title);
      // The launch store is picked by its code (which store is opening); the order of every other
      // store comes from its launch date (or launch year), oldest first. Store codes are internal
      // references and say nothing about the sequence.
      const launch = all.find(s => s.code && s.code === String(c.launchCode)) || null;
      if (!L.area) L.area = launch?.title || '';
      const opened = s => s.launchDate || (s.launchYear ? `${s.launchYear}-12-31` : '');
      // Stores without a launch date or year are left out until one is added.
      const rest = all.filter(s => s !== launch && opened(s))
        .sort((a, b) => opened(a).localeCompare(opened(b)) || a.title.localeCompare(b.title));

      const seq = rest.map((s, k) => (k === 0
        ? { ...s, chapter: chapters.first || s.launchYear, isFirst: true }
        : { ...s, chapter: s.launchYear }));
      // Placeholder stops keep the journey at exactly `number` stops when the data has gaps (as in the design).
      const pending = Math.max(0, L.number - 1 - seq.length);
      for (let k = 0; k < pending; k++) {
        seq.push({ ...this.#normalize({ title: t.pendingTitle }), handle: `pending-${k}`, mock: true, chapter: chapters.pending || '' });
      }
      if (seq.length > L.number - 1) console.warn(`[nms-sl] ${seq.length + 1} stores in the data but the launch store is number ${L.number}.`);
      seq.push(launch
        ? { ...launch, chapter: chapters.launch || '', isLaunch: true }
        : { ...this.#normalize({ title: L.area || t.pendingTitle }), handle: 'launch', chapter: chapters.launch || '', isLaunch: true });

      const live = this.isLive();
      return seq.map((s, i) => {
        const n = pad2(i + 1);
        const hasGeo = !s.mock && s.lat !== null && s.lng !== null;
        const ratingText = s.reviews > 0 ? fill(t.reviewsShort, { rating: s.rating.toFixed(1), count: s.reviews.toLocaleString('en-IN') }) : '';
        let waUrl = s.whatsapp;
        if (s.isLaunch && waUrl) waUrl += `?text=${encodeURIComponent(fill(t.waLaunchMsg, { area: L.area, date: L.dateDM }))}`;
        return {
          ...s,
          i,
          num: n,
          hasGeo,
          place: s.mock ? t.pendingPlace : [s.city, s.state].filter(Boolean).join(', '),
          label: s.mock ? t.pendingTitle : [s.title, s.city].filter(Boolean).join(', '),
          addressFull: s.mock ? t.pendingValue : [s.address, s.zip && !s.address.includes(s.zip) ? s.zip : ''].filter(Boolean).join(' · '),
          hoursText: s.hours || (s.mock ? t.pendingValue : t.notListed),
          reviewText: s.isLaunch && !live ? fill(t.opensOn, { date: L.dateLong }) : s.mock ? t.pendingValue : ratingText || t.noReviews,
          tag: s.isLaunch ? L.dateShort : s.isFirst ? t.tagFirst : s.mock ? '—' : s.reviews > 0 ? `${s.rating.toFixed(1)} ★` : t.tagNew,
          stateCode: s.mock ? '··' : STATE_CODES[s.state] || s.state.slice(0, 2).toUpperCase(),
          tel: s.phone ? `tel:${s.phone.length === 10 ? `+91${s.phone}` : s.phone}` : '',
          waUrl,
          tone: s.mock ? '#EEEDEA' : TONES[i % 4],
          photoNote: s.mock ? t.mockImage : t.awaitingImage,
        };
      });
    }

    /* ---------------- Live Google ratings ---------------- */

    /**
     * Current Google rating for a store, fetched from the Places API (New)
     * with its Place ID. Ratings are never stored: they are requested when a
     * store is shown and kept in memory for this page view only.
     * Resolves { rating, count, url } or null.
     */
    liveRating(store) {
      const key = this.config.googleKey;
      if (!key || !store?.placeId) return Promise.resolve(null);
      this.ratingCache = this.ratingCache || new Map();
      if (!this.ratingCache.has(store.placeId)) {
        const request = fetch(`https://places.googleapis.com/v1/places/${encodeURIComponent(store.placeId)}`, {
          headers: { 'X-Goog-Api-Key': key, 'X-Goog-FieldMask': 'rating,userRatingCount,googleMapsUri' },
        })
          .then(res => (res.ok ? res.json() : null))
          .then(p => (p && p.rating ? { rating: p.rating, count: p.userRatingCount || 0, url: p.googleMapsUri || '' } : null))
          .catch(() => null);
        this.ratingCache.set(store.placeId, request);
      }
      return this.ratingCache.get(store.placeId);
    }

    ratingText(live) {
      if (!live) return this.t.googleRatingNone;
      return fill(this.t.googleRating, { rating: live.rating.toFixed(1), count: live.count.toLocaleString('en-IN') });
    }

    /** Card variant: "<b>4.9</b> (53) · on Google" */
    ratingHTML(live) {
      if (!live) return esc(this.t.googleRatingNone);
      return `<b>${live.rating.toFixed(1)}</b> (${live.count.toLocaleString('en-IN')}) · on Google`;
    }

    /**
     * Fill every [data-sl-rating="<store index>"] placeholder inside root with the live Google rating.
     * When a store has no reviews the whole row is hidden: the store card (mobile sheet, desktop map
     * popup) and the store directory's details panel.
     */
    fillRatings(root) {
      root.querySelectorAll('[data-sl-rating]').forEach(el => {
        const store = this.stores[+el.dataset.slRating];
        const row = el.closest('.nms-sl-card__fact, .nms-sl-dir__fact') || el;
        const isCard = el.classList.contains('nms-sl-card__rating');
        if (!store?.placeId || !this.config.googleKey) {
          row.hidden = true;
          return;
        }
        el.textContent = this.t.googleRatingLoading;
        this.liveRating(store).then(live => {
          if (!el.isConnected) return;
          if (!live?.count) row.hidden = true;
          else if (isCard) el.innerHTML = this.ratingHTML(live);
          else el.textContent = this.ratingText(live);
        });
      });
    }

    /** Totals used by stats, legends and copy. */
    totals() {
      const real = this.stores.filter(s => !s.mock);
      const rated = real.filter(s => !s.isLaunch && s.reviews > 0);
      const reviews = rated.reduce((n, s) => n + s.reviews, 0);
      const avg = reviews ? rated.reduce((n, s) => n + s.rating * s.reviews, 0) / reviews : 0;
      const live = this.isLive();
      return {
        open: real.filter(s => !s.isLaunch).length + (live ? 1 : 0),
        stores: real.length,
        states: new Set(real.map(s => s.state).filter(Boolean)).size,
        cities: new Set(real.map(s => s.city).filter(Boolean)).size,
        reviews,
        reviewAvg: avg,
        ratedStores: rated.length,
      };
    }

    km(lat1, lng1, lat2, lng2) {
      const R = Math.PI / 180;
      return 12742 * Math.asin(Math.sqrt(Math.sin(((lat2 - lat1) * R) / 2) ** 2 + Math.cos(lat1 * R) * Math.cos(lat2 * R) * Math.sin(((lng2 - lng1) * R) / 2) ** 2));
    }

    distanceText(store) {
      const loc = this.userLocation;
      if (!loc || !store?.hasGeo) return '';
      return fill(this.t.kmFromYou, { km: Math.round(this.km(loc.lat, loc.lng, store.lat, store.lng)) });
    }

    /* ---------------- Active store ---------------- */

    select(index, source = '') {
      const i = Math.max(0, Math.min(this.lastIndex, index | 0));
      if (i === this.activeIndex && source !== 'force' && source !== 'location') return;
      this.activeIndex = i;
      // Selecting a store never changes the URL. #store-NN links only come from the Share button
      // and are still opened on page load.
      this.emit('select', { index: i, source });
    }

    /**
     * On arrival, look for the nearest store straight away (after the preloader). Skipped when the
     * visitor has blocked location for the site, or arrived on a shared #store-NN link.
     */
    async #autoLocate() {
      if (!this.config.autoLocate || !navigator.geolocation || /store-\d/.test(window.location.hash)) return;
      try {
        const status = await navigator.permissions?.query({ name: 'geolocation' });
        if (status?.state === 'denied') return;
      } catch (error) {
        /* Permissions API unavailable: ask anyway */
      }
      this.nearMe({ quiet: true });
    }

    #fromHash(initial) {
      const m = (window.location.hash || '').match(/store-(\d{1,3})/);
      if (!m) return;
      this.select(+m[1] - 1, 'hash');
      if (initial) requestAnimationFrame(() => this.scrollTo('stores'));
    }

    /* ---------------- Navigation ---------------- */

    scrollTo(anchor) {
      const el = typeof anchor === 'string' ? document.querySelector(`[data-sl-anchor="${anchor}"]`) : anchor;
      if (!el) return;
      const top = anchor === 'top' ? 0 : el.getBoundingClientRect().top + window.scrollY - 70;
      window.scrollTo({ top, behavior: this.reducedMotion ? 'auto' : 'smooth' });
    }

    /** Desktop: fly the map to a store. Mobile: open its sheet. */
    showStore(index, from = '') {
      if (this.isMobile()) {
        this.openStore(index, from);
        return;
      }
      this.select(index, from);
      this.emit('map:show', { index });
    }

    /* ---------------- Location ---------------- */

    /**
     * Nearest store from the visitor's location. `quiet` (the automatic check on arrival): no error
     * messages, and listeners should not scroll the page (see locationQuiet).
     */
    nearMe({ quiet = false } = {}) {
      const t = this.t;
      if (!navigator.geolocation) {
        if (!quiet) this.emit('location', { busy: false, message: t.locUnsupported });
        return;
      }
      this.locationQuiet = quiet;
      this.emit('location', { busy: true, message: t.locating });
      navigator.geolocation.getCurrentPosition(
        position => {
          const { latitude: lat, longitude: lng } = position.coords;
          let best = null;
          this.stores.forEach(s => {
            if (!s.hasGeo) return;
            const d = this.km(lat, lng, s.lat, s.lng);
            if (!best || d < best.d) best = { i: s.i, d };
          });
          if (!best) {
            this.emit('location', { busy: false, message: t.locNone });
            return;
          }
          this.userLocation = { lat, lng };
          // "Nearest: Store 16 · <strong>6 km away</strong>" (text escaped, only the distance is bold).
          const distance = `<strong>${esc(fill(t.locDistance, { km: Math.round(best.d) }))}</strong>`;
          const [before, after = ''] = fill(t.locNearest, { num: pad2(best.i + 1), distance: '{distance}' }).split('{distance}');
          const html = esc(before) + distance + esc(after);
          this.emit('location', { busy: false, message: fill(t.locNearest, { num: pad2(best.i + 1), distance: fill(t.locDistance, { km: Math.round(best.d) }) }), html });
          this.select(best.i, 'location');
        },
        () => this.emit('location', { busy: false, message: quiet ? '' : t.locDenied }),
        { timeout: 10000, maximumAge: 600000 }
      );
    }

    /* ---------------- Share card ---------------- */

    async #makeCard() {
      const L = this.launch;
      const sc = this.config.shareCard || {};
      const W = 1080;
      const H = 1350;
      const canvas = document.createElement('canvas');
      canvas.width = W;
      canvas.height = H;
      const x = canvas.getContext('2d');
      const display = getComputedStyle(document.documentElement).getPropertyValue('--nms-font-family-primary').trim() || "'bebas-neue-pro'";
      try {
        await Promise.all([`400 100px ${display}`, "italic 400 100px 'Roboto'"].map(f => document.fonts.load(f)));
      } catch (error) {
        /* fonts are optional */
      }
      const loadImage = src => new Promise(resolve => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src = src;
      });
      const [foot, spark] = await Promise.all([loadImage(this.config.assets.foot), loadImage(this.config.assets.spark)]);
      const city = this.launchStore?.city;
      x.fillStyle = '#EEE5D6';
      x.fillRect(0, 0, W, H);
      x.fillStyle = '#7A1214';
      x.textAlign = 'center';
      x.font = `700 44px ${display}`;
      x.fillText(sc.brand || "NEEMAN'S", W / 2, 120);
      x.font = `700 250px ${display}`;
      x.fillText(fill(this.t.store, { num: L.number }).toUpperCase(), W / 2, 400);
      x.font = "italic 400 128px 'Roboto'";
      x.fillText((sc.headline || '').toUpperCase(), W / 2, 560);
      x.font = `700 56px ${display}`;
      x.fillText(L.dateLong.toUpperCase(), W / 2, 680);
      x.font = `500 44px ${display}`;
      x.fillText([L.area, city !== L.area ? city : ''].filter(Boolean).join(', ').toUpperCase(), W / 2, 744);
      if (foot) {
        for (let i = 0; i < 8; i++) {
          const u = i / 7;
          const px = 110 + u * 860;
          const py = 1180 - u * 300 - Math.sin(u * Math.PI) * 60;
          const a = Math.atan2(-300 - Math.cos(u * Math.PI) * 60 * Math.PI, 860);
          const side = i % 2 ? 22 : -22;
          x.save();
          x.translate(px + Math.sin(a) * side, py - Math.cos(a) * side);
          x.rotate(a);
          x.drawImage(foot, -70, -30, 140, 60);
          x.restore();
        }
      }
      if (spark) {
        [[180, 1040, 40], [520, 900, 30], [840, 960, 46], [990, 760, 34], [300, 1240, 28]].forEach(([sx, sy, s]) => x.drawImage(spark, sx - s / 2, sy - s * .65, s, s * 1.3));
      }
      x.font = `500 36px ${display}`;
      x.fillText((sc.footer || '').toUpperCase(), W / 2, 1300);
      return new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
    }

    /** "I'll be there" card: native share sheet where files are supported, download elsewhere. */
    async shareCard() {
      if (this.cardBusy) return;
      this.cardBusy = true;
      this.emit('card', { busy: true });
      const L = this.launch;
      try {
        const blob = await this.#makeCard();
        if (!blob) return;
        const file = new File([blob], `neemans-store-${L.number}.png`, { type: 'image/png' });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: fill(this.t.shareCardTitle, { number: L.number }), text: fill(this.t.shareCardText, { area: L.area, date: L.dateDM }) }).catch(() => {});
        } else {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = file.name;
          document.body.appendChild(a);
          a.click();
          a.remove();
          setTimeout(() => URL.revokeObjectURL(url), 4000);
        }
        this.track('store_share_card', { store: L.number });
      } finally {
        this.cardBusy = false;
        this.emit('card', { busy: false });
      }
    }

    /** Share a single store link (#store-NN). Resolves true when the link was copied. */
    async shareStore(store) {
      const L = this.launch;
      const url = `${location.origin}${location.pathname}#store-${store.num}`;
      const data = {
        title: fill(this.t.shareStoreTitle, { num: store.num, label: store.label }),
        text: store.isLaunch
          ? fill(this.t.shareLaunchText, { ordinal: L.ordinal, date: L.dateDM, label: store.label })
          : fill(this.t.shareStoreText, { num: store.num, label: store.label }),
        url,
      };
      if (navigator.share) {
        await navigator.share(data).catch(() => {});
        return false;
      }
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(url);
        return true;
      }
      return false;
    }

    /* ---------------- Bottom sheet ---------------- */

    #bindSheet() {
      const sheet = document.querySelector('[data-sl-sheet]');
      if (!sheet) return;
      this.sheet = {
        root: sheet,
        panel: sheet.querySelector('[role="dialog"]'),
        title: sheet.querySelector('#NmsSlSheetTitle'),
        sub: sheet.querySelector('[data-sl-sheet-sub]'),
        back: sheet.querySelector('[data-sl-sheet-back]'),
        body: sheet.querySelector('[data-sl-sheet-body]'),
        icon: sheet.querySelector('[data-sl-sheet-icon]'),
        kind: null,
        from: null,
        stateName: '',
      };
      sheet.addEventListener('touchmove', event => {
        if (!this.scrollLocked) return;
        const panel = this.sheet.panel;
        const scroller = event.target.closest('.nms-sl-sheet__panel, .nms-sl-scroller, [data-photos]');
        if (!scroller || !panel.contains(scroller) || scroller.scrollHeight <= scroller.clientHeight && scroller.scrollWidth <= scroller.clientWidth) event.preventDefault();
      }, { passive: false });
      sheet.addEventListener('click', event => {
        if (this.onStoreCardClick(event)) return;
        // The scrim covers the map's "All India" button: a tap there closes the sheet fully and
        // zooms out, instead of stepping back to the state list.
        if (event.target.classList.contains('nms-sl-sheet__scrim')) {
          const allIndia = document.elementsFromPoint(event.clientX, event.clientY)
            .find(el => !sheet.contains(el))?.closest('.nms-sl-map__back');
          if (allIndia) {
            this.closeSheet(true);
            allIndia.click();
            return;
          }
        }
        if (event.target.closest('[data-sl-sheet-close]')) this.closeSheet();
        else if (event.target.closest('[data-sl-sheet-back]')) this.openState(this.sheet.stateName, true);
        const open = event.target.closest('[data-sl-open-store]');
        if (open) this.openStore(+open.dataset.slOpenStore, 'state');
        const action = event.target.closest('[data-sl-visit]')?.dataset.slVisit;
        if (action === 'near') {
          this.closeSheet();
          this.nearMe();
          this.scrollTo('stores');
        } else if (action === 'film') {
          this.closeSheet();
          this.film?.open();
        } else if (action === 'card') {
          this.shareCard();
        }
      });
      document.addEventListener('keydown', event => {
        if (event.key === 'Escape' && this.sheet.kind) this.closeSheet();
      });
      this.on('card', ({ busy }) => {
        const label = this.sheet.body.querySelector('[data-sl-visit="card"] span');
        if (label) label.textContent = busy ? this.t.shareCardBusy : this.t.shareCard;
      });
    }

    /**
     * Keep the page behind a modal drawer (store, visit) from scrolling. The state list is not
     * modal (no scrim, the map stays usable), so it leaves the page scrollable.
     */
    #lockScroll(on) {
      if (on === !!this.scrollLocked) return;
      this.scrollLocked = on;
      document.documentElement.classList.toggle('nms-sl-scroll-lock', on);
    }

    #showSheet(kind, title, sub, html, iconSrc = '') {
      const S = this.sheet;
      if (!S) return;
      this.#lockScroll(kind !== 'state');
      const wasOpen = !!S.kind;
      if (!wasOpen) this.lastFocus = document.activeElement;
      S.kind = kind;
      const hasBack = kind === 'store' && S.from === 'state';
      S.root.className = `nms-sl nms-sl-sheet nms-sl-sheet--${kind}${hasBack ? ' nms-sl-sheet--has-back' : ''}`;
      S.title.textContent = title;
      if (S.icon) {
        S.icon.hidden = !iconSrc;
        S.icon.innerHTML = iconSrc ? `<img src="${esc(iconSrc)}" alt="" width="24" height="24">` : '';
      }
      S.sub.textContent = sub;
      S.body.innerHTML = html;
      S.back.hidden = !hasBack;
      // Caret icon instead of the label's own "←".
      S.back.innerHTML = `${icon('caret-left', 16)}<span>${esc(fill(this.t.backToState, { state: S.stateName }).replace(/^\s*←\s*/, ''))}</span>`;
      S.root.hidden = false;
      S.panel.scrollTop = 0;
      requestAnimationFrame(() => {
        S.root.classList.add('is-open');
        S.panel.focus({ preventScroll: true });
      });
      this.emit('sheet', { open: true, kind });
    }

    /** Closes the sheet; a store opened from a state list goes back to that list unless `all` is set. */
    closeSheet(all = false) {
      const S = this.sheet;
      if (!S?.kind) return;
      if (!all && S.kind === 'store' && S.from === 'state') {
        this.openState(S.stateName, true);
        return;
      }
      const kind = S.kind;
      S.kind = null;
      this.#lockScroll(false);
      S.root.classList.remove('is-open');
      window.setTimeout(() => {
        if (!S.kind) S.root.hidden = true;
      }, this.reducedMotion ? 0 : 320);
      if (kind === 'state') this.emit('map:zoom', { state: null });
      this.emit('sheet', { open: false, kind });
      this.lastFocus?.focus?.({ preventScroll: true });
    }

    openVisit() {
      const t = this.t;
      const L = this.launch;
      const live = this.isLive();
      const store = this.launchStore || {};
      const tile = (cls, href, iconName, label, attrs = '') =>
        href
          ? `<a class="nms-sl-visit-tile ${cls}" href="${esc(href)}"${/^https?:/.test(href) ? ' target="_blank" rel="noopener"' : ''} ${attrs}>${icon(iconName, 20)}<span>${esc(label)}</span></a>`
          : `<button type="button" class="nms-sl-visit-tile ${cls}" ${attrs}>${icon(iconName, 20)}<span>${esc(label)}</span></button>`;
      const map = store.map || `https://www.google.com/maps/search/${encodeURIComponent(`Neeman's ${L.area}`)}`;
      const tiles = [
        live ? tile('nms-sl-visit-tile--red', map, 'navigate', t.getDirections) : tile('nms-sl-visit-tile--maroon', this.calendarUrl(), 'calendar', t.addToCalendar),
        live ? '' : tile('', map, 'pin', t.googleMaps),
        tile('', store.waUrl || this.whatsappUrl(), 'whatsapp', live ? t.waStore : t.waReminder),
        tile('', '', 'navigate', t.nearestStore, 'data-sl-visit="near"'),
        tile('', '', 'share', this.cardBusy ? t.shareCardBusy : t.shareCard, 'data-sl-visit="card"'),
        this.film ? tile('', '', 'film', t.watchFilm, 'data-sl-visit="film"') : '',
        store.tel ? tile('', store.tel, 'phone', t.callStore) : '',
      ].join('');
      this.sheet && (this.sheet.from = null);
      this.#showSheet('visit', t.planVisit, fill(t.visitSub, { number: L.number, area: L.area, date: L.dateLong }), `<div class="nms-sl-visit-grid">${tiles}</div>`);
    }

    /* ---------------- Store card (Figma "nmns2/Dialog") ---------------- */

    /** "Neeman's Punjagutta, Hyderabad" */
    storeCardTitle(s) {
      const name = [s.title, s.city && s.city !== s.title ? s.city : ''].filter(Boolean).join(', ');
      return fill(this.t.cardTitle, { name });
    }

    /** "Store 50 · Punjagutta, Telangana" */
    storeCardSub(s) {
      const place = s.mock ? this.t.pendingPlace : [s.title, s.state].filter(Boolean).join(', ');
      return fill(this.t.cardSub, { num: s.num, place });
    }

    /**
     * Card body: photo, facts (address, hours, live Google rating) and actions.
     * Same markup in the mobile sheet and the desktop map popup; actions are
     * handled by onStoreCardClick().
     */
    storeCardBody(s) {
      const t = this.t;
      const photo = s.images[0];
      const media = photo
        ? `<img class="nms-sl-card__photo" src="${esc(photo.src)}" alt="${esc(photo.alt || s.title)}" width="686" height="374" loading="lazy" decoding="async">`
        : `<div class="nms-sl-card__photo nms-sl-placeholder" style="--tone:${s.tone}"><span class="nms-sl-placeholder__note">${esc(s.photoNote)}</span></div>`;
      const fact = (iconName, html) => `<div class="nms-sl-card__fact">${icon(iconName, 16)}<span>${html}</span></div>`;
      const link = (cls, href, iconName, label) =>
        href ? `<a class="nms-sl-card__btn ${cls}" href="${esc(href)}"${/^https?:/.test(href) ? ' target="_blank" rel="noopener"' : ''}>${icon(iconName, 16)}<span>${esc(label)}</span></a>` : '';
      return `${media}
        <div class="nms-sl-card__facts">
          ${s.address ? fact('pin', esc(s.addressFull)) : ''}
          ${s.mock ? fact('clock', esc(t.pendingStory)) : fact('clock', esc(s.hoursText))}
          ${s.mock ? '' : fact('star-outline', `<span class="nms-sl-card__rating" data-sl-rating="${s.i}"></span>`)}
        </div>
        <div class="nms-sl-card__actions">
          <div class="nms-sl-card__row">
            ${link('nms-sl-card__btn--dark', s.map, 'navigate', t.cardDirections)}
            ${link('', s.waUrl, 'whatsapp', t.cardWhatsapp)}
          </div>
          <div class="nms-sl-card__row">
            ${link('', s.tel, 'phone', t.cardCall)}
            <button type="button" class="nms-sl-card__btn nms-sl-card__btn--icon" data-sl-card="share" data-store="${s.i}" aria-label="${esc(t.cardShare)}">${icon('share-network', 20)}</button>
          </div>
        </div>`;
    }

    /** Handles Share inside any store card. Returns true when handled. */
    onStoreCardClick(event) {
      const button = event.target.closest('[data-sl-card]');
      if (!button) return false;
      const store = this.stores[+button.dataset.store];
      if (!store) return true;
      this.shareStore(store).then(copied => {
        if (copied) button.setAttribute('aria-label', this.t.linkCopied);
      });
      return true;
    }

    openStore(index, from = '') {
      const s = this.stores[index];
      if (!s || !this.sheet) return;
      this.select(index, from || 'sheet');
      this.sheet.from = from;
      // "All in {state}" always names (and goes back to) this store's own state.
      if (from === 'state') this.sheet.stateName = s.state;
      // Figma 32263:99672: no state icon when the back link is shown.
      this.#showSheet('store', this.storeCardTitle(s), this.storeCardSub(s), `<div class="nms-sl-card__body">${this.storeCardBody(s)}</div>`, from === 'state' ? '' : this.stateIcon(s.state));
      this.fillRatings(this.sheet.body);
    }

    /** PNG for a state from Launch · Map's "State icon" blocks ('' when there is none). */
    stateIcon(name) {
      if (!this.stateIcons) {
        this.stateIcons = new Map();
        document.querySelectorAll('[data-nms-sl-state-icons] [data-state]').forEach(el => {
          this.stateIcons.set(el.dataset.state.trim().toLowerCase(), el.dataset.src);
        });
      }
      return this.stateIcons.get(String(name || '').trim().toLowerCase()) || '';
    }

    /** Round badge with the state's icon, or '' when the state has none. */
    stateBadge(name) {
      const src = this.stateIcon(name);
      return src ? `<span class="nms-sl-state-badge" aria-hidden="true"><img src="${esc(src)}" alt="" width="24" height="24"></span>` : '';
    }

    openState(name, fromBack = false) {
      if (!this.sheet) return;
      const t = this.t;
      const list = this.stores.filter(s => !s.mock && s.state === name);
      this.sheet.stateName = name;
      this.sheet.from = null;
      const html = `<div class="nms-sl-state-list">${list.map(s => `
        <button type="button" class="nms-sl-state-item" data-sl-open-store="${s.i}">
          <span class="nms-sl-state-item__num">${s.num}</span>
          <span class="nms-sl-state-item__text"><span class="nms-sl-state-item__title">${esc(s.title)}</span><span class="nms-sl-state-item__line">${[s.city && esc(s.city), s.hoursText && `<span class="nms-sl-state-item__hours">${esc(s.hoursText.replace(/:00(?=\s*[ap]m)/gi, ''))}</span>`].filter(Boolean).join(' · ')}</span></span>
          <span class="nms-sl-state-item__chev" aria-hidden="true">${icon('caret-right', 20)}</span>
        </button>`).join('')}</div>`;
      this.#showSheet('state', name, list.length === 1 ? t.stateCountOne : fill(t.stateCount, { count: list.length }), html, this.stateIcon(name));
      if (!fromBack) {
        this.emit('map:zoom', { state: name });
        this.scrollTo('map-canvas');
      }
    }

    /* ---------------- Analytics ---------------- */

    #bindTracking() {
      document.addEventListener('click', event => {
        const a = event.target.closest('.nms-sl a, .nms-sl button');
        if (!a) return;
        const href = a.getAttribute('href') || '';
        const action = a.dataset.track || (/^tel:/.test(href) ? 'call' : /wa\.me/.test(href) ? 'whatsapp' : /google\.[^/]+\/maps|maps\.google|maps\?cid/.test(href) ? 'directions' : '');
        if (action) this.track('store_action', { action, href });
      }, true);
    }

    /* ---------------- Components ---------------- */

    /**
     * Instantiate a section component for every matching element now and on
     * theme editor section reloads; destroy it when the section is removed.
     */
    register(selector, Component) {
      const boot = root => {
        root.querySelectorAll(selector).forEach(el => {
          if (this.instances.has(el)) return;
          const instance = new Component(el, this);
          this.instances.set(el, instance);
          instance.init();
        });
      };
      boot(document);
      if (!window.Shopify?.designMode) return;
      document.addEventListener('shopify:section:load', event => boot(event.target));
      document.addEventListener('shopify:section:unload', event => {
        event.target.querySelectorAll(selector).forEach(el => {
          this.instances.get(el)?.destroy();
          this.instances.delete(el);
        });
      });
    }

    /**
     * Links marked data-sl-link="map|calendar|tel|whatsapp" whose href was left
     * blank ("#") in the editor point at the launch store. tel links without a
     * phone number are hidden.
     */
    fillLinks(root = document) {
      const store = this.launchStore || {};
      const urls = {
        map: store.map || `https://www.google.com/maps/search/${encodeURIComponent(`Neeman's ${this.launch.area}`)}`,
        calendar: this.calendarUrl(),
        tel: store.tel,
        whatsapp: store.waUrl || this.whatsappUrl(),
      };
      root.querySelectorAll('.nms-sl [data-sl-link], .nms-sl[data-sl-link]').forEach(link => {
        const url = urls[link.dataset.slLink];
        const current = link.getAttribute('href');
        if ((!current || current === '#') && url) link.setAttribute('href', url);
        if (link.dataset.slLink === 'tel') link.hidden = !url;
      });
    }

    /** Called after the first components registered, so deep links land on live sections. */
    start() {
      if (this.started) return;
      this.started = true;
      this.fillLinks();
      if (window.Shopify?.designMode) document.addEventListener('shopify:section:load', event => this.fillLinks(event.target));
      this.#fromHash(true);
      if (!window.Shopify?.designMode) this.afterIntro('done', () => this.#autoLocate());
    }
  }

  const config = readJSON('NmsSlConfig');
  const records = readJSON('NmsSlStores');
  if (!config) {
    console.warn('[nms-sl] Store launch settings section is missing from this page.');
    return;
  }
  window.neemans.storeLaunch = new NMSStoreLaunch(config, records || []);
  document.dispatchEvent(new CustomEvent('nms-sl:ready'));
  // Deferred section scripts all run before DOMContentLoaded; resolve #store-NN deep links after they registered.
  if (document.readyState === 'loading' || document.readyState === 'interactive') {
    document.addEventListener('DOMContentLoaded', () => window.neemans.storeLaunch.start(), { once: true });
  } else {
    window.neemans.storeLaunch.start();
  }
})();
